import React from 'react';
import Tag from '@ui/tag/Tag';
import { BookingPositionHotel, BookingPositionState } from './model/BookingPositionState';
import {
	compsetPlaceText,
	euroText,
	HEAD_SLOTS,
	hasChangeHistory,
	middleHalfText,
	otherCityHotels,
	percentText,
	PlaceChange,
	placeChange,
	positionText,
	rankValue,
	reviewChange,
	sameCityHotels,
	scoreText,
	windowText,
} from './BookingRanking.utilities';
import { BadgeKey, HotelNameCell, KeyItem, KeyList, RankingCard, RankingCardHead, TD, TH } from './BookingRankingParts';
import RankTierChip from '@ui/rank/RankTierChip';

export const typicalKeys = (): KeyItem[] => [
	{ term: 'Typical', text: `the middle position of all checks on the nights when the hotel is on sale. A check that does not find the hotel in the first ${HEAD_SLOTS} results counts as not in the top 500.` },
	{ term: 'Middle half', text: 'half of the checks put the hotel in this range ("or worse" = the range ends outside the top 500).' },
];

export const LEVEL_KEY: KeyItem = { term: '≈', text: 'level with the hotel above or below it. Each one is higher in the list on about half of the nights (40–60%).' };

export const priceKeys = (city: string): KeyItem[] => [
	{ term: 'Price rank', text: `the place by price on a typical night among your compset hotels in ${city} on sale (1 = the cheapest). Two hotels can share a place.` },
	{ term: 'City: dearer than', text: `about the share of the properties on sale in ${city} that are cheaper. Booking.com's price chart counts prices in steps of a few euros; RoomPulse counts half of your own step as cheaper.` },
];

export const ORDER_KEY: KeyItem = { text: 'Each column has its own middle value, so a hotel can sit out of order in one column.' };

// Change vs 4 weeks: plain text in the green and red of the review change (decision of 2026-10-05: not a
// new component). Steady = a move of not more than half the middle half (at least 2 places).
const PlaceChangeText: React.FunctionComponent<{ change: PlaceChange | null }> = ({ change }) => {
	if (change === null) return <span className="text-gray-500">–</span>;
	if (change.kind === 'steady') {
		return <span title="Steady: a move of not more than half the middle half (at least 2 places)"
			className="whitespace-nowrap font-semibold text-gray-600">Steady</span>;
	}
	const up = change.kind === 'up';
	return <span title={`${up ? 'Up' : 'Down'} ${change.places} places vs the same 7 days 4 weeks earlier`}
		className={`whitespace-nowrap font-semibold ${up ? 'text-green-700' : 'text-red-700'}`}>
		{up ? '▲' : '▼'} {change.places}
	</span>;
};

const ReviewCell: React.FunctionComponent<{ hotel: BookingPositionHotel, history: boolean }> = ({ hotel, history }) => {
	const change = reviewChange(hotel);
	return <span className="inline-flex items-baseline gap-1.5 whitespace-nowrap">
		{scoreText(hotel.reviewScore)}
		{!history && hotel.reviewScore !== null && <span className="text-xs text-gray-500">New</span>}
		{history && change !== null && <span className={`text-xs font-semibold ${change > 0 ? 'text-green-700' : 'text-red-700'}`}>
			{change > 0 ? '+' : '−'}{Math.abs(change).toFixed(1)}
		</span>}
	</span>;
};

// The compset table: the competitors in the Booking city of the hotel only (D10), in the order of the
// compset place. After 4 weeks of daily scans it adds "Change vs 4 weeks" (D11).
const CompsetTable: React.FunctionComponent<{ state: BookingPositionState }> = ({ state }) => {
	const hotels = sameCityHotels(state);
	const others = otherCityHotels(state);
	const city = state.bookingCity.name;
	const history = hasChangeHistory(state);
	const keys: KeyItem[] = [
		...typicalKeys(),
		...(hotels.some((h) => h.levelWithNeighbour) ? [LEVEL_KEY] : []),
		...(history ? [{ term: 'Change', text: 'the typical position of the last 7 days against the same 7 days 4 weeks earlier. Steady: a move of not more than half the middle half (at least 2 places).' }] : []),
		...priceKeys(city),
		ORDER_KEY,
		...(others.length > 0 ? [{ text: `Your compset has ${others.length} more hotels in another Booking city. They are in the block below and do not count in the places of this table.` }] : []),
	];

	return <RankingCard>
		<RankingCardHead
			title="Your compset"
			subtitle={`${hotels.length} hotels in ${city} · all ${state.nights.length} nights of the next 6 months${state.scan !== null ? ` · ${windowText(state.scan)}` : ''}`} />
		<div className="overflow-x-auto">
			<table className="min-w-full">
				<thead><tr className="border-b border-gray-200">
					<th className={`${TH} w-12`}>#</th>
					<th className={TH}>Hotel</th>
					<th className={`${TH} hidden md:table-cell`}>Visibility</th>
					<th className={`${TH} text-right`}>Typical</th>
					<th className={`${TH} hidden sm:table-cell`}>Middle half</th>
					{history && <th className={TH}>Change<span className="hidden sm:inline"> vs 4 weeks</span></th>}
					<th className={`${TH} text-right ${history ? 'hidden sm:table-cell' : ''}`}>Price rank</th>
					<th className={`${TH} hidden text-right sm:table-cell`}>City: dearer than</th>
					<th className={`${TH} hidden text-right sm:table-cell`}>Typical price</th>
					<th className={`${TH} hidden sm:table-cell`}>{history ? 'Review · change' : 'Review score'}</th>
				</tr></thead>
				<tbody className="divide-y divide-gray-100">
					{hotels.map((hotel) => <tr key={hotel.hotelId} className={hotel.isMain ? 'bg-sky-50' : ''}>
						<td className={`${TD} whitespace-nowrap text-gray-600`}>{compsetPlaceText(hotel)}</td>
						<td className={TD}>
							<HotelNameCell hotel={hotel}
								sub={history
									? `Price rank ${hotel.compsetPriceRank ?? '–'} · city: dearer than ${percentText(hotel.cityDearerShare)} · Review ${scoreText(hotel.reviewScore)}`
									: `City: dearer than ${percentText(hotel.cityDearerShare)} · ${euroText(hotel.typicalPrice)} · Review ${scoreText(hotel.reviewScore)}`} />
						</td>
						<td className={`${TD} hidden md:table-cell`}>
							<RankTierChip value={rankValue(hotel.typical, hotel.typicalAfter500)} />
						</td>
						<td className={`${TD} whitespace-nowrap text-right font-bold`}>{positionText(hotel.typical, hotel.typicalAfter500)}</td>
						<td className={`${TD} hidden whitespace-nowrap text-gray-600 sm:table-cell`}>{middleHalfText(hotel)}</td>
						{history && <td className={TD}><PlaceChangeText change={placeChange(hotel)} /></td>}
						<td className={`${TD} text-right font-semibold ${history ? 'hidden sm:table-cell' : ''}`}>{hotel.compsetPriceRank ?? '–'}</td>
						<td className={`${TD} hidden text-right sm:table-cell`}>{percentText(hotel.cityDearerShare)}</td>
						<td className={`${TD} hidden text-right sm:table-cell`}>{euroText(hotel.typicalPrice)}</td>
						<td className={`${TD} hidden sm:table-cell`}><ReviewCell hotel={hotel} history={history} /></td>
					</tr>)}
				</tbody>
			</table>
		</div>
		<div className="flex flex-col gap-2 border-t border-gray-100 px-4 py-3 sm:px-5">
			<BadgeKey />
			<KeyList items={keys} />
		</div>
	</RankingCard>;
};

// D10: the compset hotels in another Booking city, with their Typical position in their own city list.
export const OtherCityTable: React.FunctionComponent<{ state: BookingPositionState }> = ({ state }) => {
	const hotels = otherCityHotels(state);
	if (hotels.length === 0) return null;
	const city = state.bookingCity.name;
	const cities = [...new Set(hotels.map((h) => h.bookingCityName))].sort().join(' and ');
	return <RankingCard>
		<RankingCardHead title="In another Booking city"
			subtitle={`${hotels.length} hotels of your compset${state.scan !== null ? ` · ${windowText(state.scan)}` : ''}`} />
		<div className="overflow-x-auto">
			<table className="min-w-full">
				<thead><tr className="border-b border-gray-200">
					<th className={TH}>Hotel</th>
					<th className={`${TH} hidden sm:table-cell`}>Booking city</th>
					<th className={`${TH} text-right`}>Typical position</th>
					<th className={`${TH} hidden text-right sm:table-cell`}>Typical price</th>
					<th className={`${TH} hidden sm:table-cell`}>Review score</th>
				</tr></thead>
				<tbody className="divide-y divide-gray-100">
					{hotels.map((hotel) => <tr key={hotel.hotelId}>
						<td className={TD}><HotelNameCell hotel={hotel}
							sub={`Typical price ${euroText(hotel.typicalPrice)} · Review ${scoreText(hotel.reviewScore)}`} /></td>
						<td className={`${TD} hidden sm:table-cell`}><Tag state="slate">{hotel.bookingCityName}</Tag></td>
						<td className={`${TD} whitespace-nowrap text-right font-bold`}>
							{positionText(hotel.typical, hotel.typicalAfter500)} · {hotel.bookingCityName}
						</td>
						<td className={`${TD} hidden text-right sm:table-cell`}>{euroText(hotel.typicalPrice)}</td>
						<td className={`${TD} hidden sm:table-cell`}>{scoreText(hotel.reviewScore)}</td>
					</tr>)}
				</tbody>
			</table>
		</div>
		<div className="border-t border-gray-100 px-4 py-3 sm:px-5">
			<KeyList items={[
				{ text: `Booking.com lists these hotels under ${cities}, not under ${city}. A guest who searches ${city} does not see them.` },
				{ term: 'Typical position', text: `the middle position of all checks in the ${cities} list (${cities} is a Scan city). RoomPulse does not rank these hotels against you, and they do not count in your compset places or price rank.` },
			]} />
		</div>
	</RankingCard>;
};

export default CompsetTable;
